'use client';

import React, {useState, useEffect, useContext} from 'react';
import {motion} from 'framer-motion';
import useTopStyle from 'utils/useTopStyle';
import {ThemeContext} from 'utils/ThemeContext';
import BookBtn from '../buttons/BookBtn/BookBtn';
import ArrowLink from '../buttons/ArrowLink/ArrowLink';
import HomeHeroAnim from './HomeHeroAnim/HomeHeroAnim';
import heroVariants from 'animation/heroVariants';
import spanVariants from 'animation/spanVariants';

const TypeWriter = ({messages = [], typingSpeed = 80, eraseSpeed = 10, pause = 2400}) => {
  const [msgIndex, setMsgIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [display, setDisplay] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cursorVisible, setCursorVisible] = useState(true);

  useEffect(() => {
    const id = setInterval(() => setCursorVisible(v => !v), 500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!messages || messages.length === 0) return;
    const current = messages[msgIndex] || '';
    if (!isDeleting) {
      if (charIndex < current.length) {
        const t = setTimeout(() => {
          setDisplay(current.slice(0, charIndex + 1));
          setCharIndex(c => c + 1);
        }, typingSpeed);
        return () => clearTimeout(t);
      } else {
        const t = setTimeout(() => setIsDeleting(true), pause);
        return () => clearTimeout(t);
      }
    } else {
      if (charIndex > 0) {
        const t = setTimeout(() => {
          setDisplay(current.slice(0, charIndex - 1));
          setCharIndex(c => c - 1);
        }, eraseSpeed);
        return () => clearTimeout(t);
      } else {
        setIsDeleting(false);
        setMsgIndex(i => (i + 1) % messages.length);
      }
    }
  }, [charIndex, isDeleting, msgIndex, messages, typingSpeed, eraseSpeed, pause]);

  return (
    <>
      {display}
      <span style={{
        display: 'inline-block',
        width: 1,
        marginLeft: 6,
        backgroundColor: 'currentColor',
        height: '1em',
        verticalAlign: 'text-bottom',
        visibility: cursorVisible ? 'visible' : 'hidden'
      }} />
    </>
  );
};

const PlatformIcon = ({color = 'currentColor'}) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{flex: '0 0 16px'}}>
    <rect x="3" y="3" width="7" height="7" stroke={color} strokeWidth="1.6" rx="1" />
    <rect x="14" y="3" width="7" height="7" stroke={color} strokeWidth="1.6" rx="1" />
    <rect x="3" y="14" width="7" height="7" stroke={color} strokeWidth="1.6" rx="1" />
    <rect x="14" y="14" width="7" height="7" stroke={color} strokeWidth="1.6" rx="1" />
  </svg>
);

const DataIcon = ({color = 'currentColor'}) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{flex: '0 0 16px'}}>
    <ellipse cx="12" cy="6" rx="8" ry="3" stroke={color} strokeWidth="1.6" />
    <path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6" stroke={color} strokeWidth="1.6" />
    <path d="M4 12v2c0 1.7 3.6 3 8 3s8-1.3 8-3v-2" stroke={color} strokeWidth="1.2" opacity="0.8" />
  </svg>
);

const LayersIcon = ({color = 'currentColor'}) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{flex: '0 0 16px'}}>
    <path d="M12 3l9 6-9 6-9-6 9-6z" stroke={color} strokeWidth="1.6" />
    <path d="M3 15l9 6 9-6" stroke={color} strokeWidth="1.6" opacity="0.95" />
    <path d="M3 11l9 6 9-6" stroke={color} strokeWidth="1.2" opacity="0.7" />
  </svg>
);

const Chip = ({icon, children, iconColor, bgLight = 'rgba(0, 0, 0, 0.08)', bgDark = 'rgba(255, 255, 255, 0.12)'}) => {
  const {theme} = useContext(ThemeContext);
  const isDark = theme === 'dark';

  const chipStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 10px',
    borderRadius: 999,
    background: isDark ? bgDark : bgLight,
    color: 'inherit',
    fontSize: 13,
    lineHeight: 1,
    gap: 8,
    boxShadow: isDark ? 'none' : 'none'
  };

  return (
    <span  style={chipStyle}>
      <span style={{display:'inline-flex', alignItems:'center', justifyContent:'center'}}>
        {React.isValidElement(icon) ? React.cloneElement(icon, {color: iconColor || 'currentColor'}) : icon}
      </span>
      <span style={{marginLeft: 0}}>{children}</span>
    </span>
  );
};

const HomeHero = (props) => {
  const ref = useTopStyle(72);
  const {title, text, highlight} = props;

  return (
    <motion.section
      className='home-hero'
      ref={ref}
      initial={'hidden'}
      animate={'visible'}
      transition={{
        duration: 0.7,
        delay: 0.5,
        ease: 'easeInOut'
      }}
      variants={heroVariants}
    >
      <div className='container'>
        <div className='content'>


          <div className="chips" style={{display: 'flex', gap: 8, marginBottom: 12}}>
            <Chip icon={<PlatformIcon />} iconColor="#06B6D4">One Platform</Chip>
            <Chip icon={<DataIcon />} iconColor="#6366F1">One Data Fabric</Chip>
            <Chip icon={<LayersIcon />} iconColor="#F97316">One Operating Layer</Chip>
          </div>

          <h2 className='title'>
            {title}
            <span className='arrow-right'></span>
            <motion.span
              className='highlight'
              style={{display: 'block'}}
              initial={'hidden'}
              animate={'visible'}
              transition={{
                duration: 0.8,
                delay: 0.8,
                ease: 'easeInOut'
              }}
              variants={spanVariants}
            >
              <TypeWriter messages={highlight} />
            </motion.span>
          </h2>
          <p className='text'>{text}</p>
          <div className='buttons'>
            <BookBtn />
            <ArrowLink text={"View Platform"} url={"platform/overview/"}/>
          </div>
        </div>
        <div className='design'>
          <HomeHeroAnim />
        </div>
      </div>
    </motion.section>
  );
};

export default HomeHero;